/**
 * Support Reply Composer – tabbed interface for reply vs internal note
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '@respectly/ui';
import { Send, Lock, Paperclip, X, CornerDownLeft } from 'lucide-react';

export type PendingAttachment = { url: string; fileName: string; fileSize?: number };

const MAX_ATTACHMENTS = 5;

interface SupportReplyComposerProps {
  onSubmit: (content: string, isInternal: boolean, attachments?: PendingAttachment[]) => Promise<boolean>;
  onUpload?: (file: File) => Promise<{ url: string; fileName: string; fileSize?: number }>;
  isDisabled?: boolean;
  className?: string;
}

export function SupportReplyComposer({
  onSubmit,
  onUpload,
  isDisabled,
  className,
}: SupportReplyComposerProps) {
  const [content, setContent] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!content.trim() || isSending || isDisabled) return;
    setIsSending(true);
    const success = await onSubmit(
      content.trim(),
      isInternal,
      pendingAttachments.length > 0 ? pendingAttachments : undefined
    );
    setIsSending(false);
    if (success) {
      setContent('');
      setIsInternal(false);
      setPendingAttachments([]);
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
      textareaRef.current?.focus();
    }
  }, [content, isInternal, isSending, isDisabled, pendingAttachments, onSubmit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setContent(e.target.value);
    const ta = e.target;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`;
  };

  const handleFileSelect = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !onUpload) return;
      if (pendingAttachments.length >= MAX_ATTACHMENTS) return;
      e.target.value = '';
      try {
        setIsUploading(true);
        const result = await onUpload(file);
        setPendingAttachments((prev) => [...prev, result].slice(0, MAX_ATTACHMENTS));
      } finally {
        setIsUploading(false);
      }
    },
    [onUpload, pendingAttachments.length]
  );

  const removePendingAttachment = (index: number) => {
    setPendingAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const canSend = content.trim().length > 0 && !isSending && !isDisabled;

  return (
    <div
      className={cn(
        'border-t border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900',
        className
      )}
    >
      {/* Tab switcher */}
      <div className="flex border-b border-zinc-100 dark:border-zinc-800">
        <button
          type="button"
          onClick={() => setIsInternal(false)}
          className={cn(
            'px-4 py-2.5 text-xs font-semibold transition-colors border-b-2 -mb-px',
            !isInternal
              ? 'border-blue-500 text-blue-600 dark:text-blue-400'
              : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
          )}
        >
          Reply to Customer
        </button>
        <button
          type="button"
          onClick={() => setIsInternal(true)}
          className={cn(
            'px-4 py-2.5 text-xs font-semibold transition-colors border-b-2 -mb-px flex items-center gap-1.5',
            isInternal
              ? 'border-amber-500 text-amber-600 dark:text-amber-400'
              : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300'
          )}
        >
          <Lock className="h-3 w-3" />
          Internal Note
        </button>
      </div>

      <div className={cn('p-4', isInternal && 'bg-amber-50/30 dark:bg-amber-900/10')}>
        {/* Pending attachments */}
        {pendingAttachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {pendingAttachments.map((a, i) => (
              <span
                key={`${a.url}-${i}`}
                className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-100 dark:bg-zinc-800 px-2.5 py-1.5 text-xs ring-1 ring-zinc-200 dark:ring-zinc-700"
              >
                <Paperclip className="h-3 w-3 text-zinc-400" />
                <span className="truncate max-w-[140px] text-zinc-700 dark:text-zinc-300">{a.fileName}</span>
                <button
                  type="button"
                  onClick={() => removePendingAttachment(i)}
                  className="p-0.5 rounded hover:bg-zinc-200 dark:hover:bg-zinc-700"
                >
                  <X className="h-3 w-3 text-zinc-400" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Internal note notice */}
        {isInternal && (
          <div className="mb-3 flex items-center gap-2 rounded-lg bg-amber-100 dark:bg-amber-900/30 px-3 py-2 ring-1 ring-amber-200 dark:ring-amber-800">
            <Lock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400 shrink-0" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              This note is only visible to support admins — not the customer.
            </p>
          </div>
        )}

        {/* Textarea + send */}
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleTextareaChange}
              onKeyDown={handleKeyDown}
              placeholder={
                isInternal
                  ? 'Write an internal note...'
                  : 'Write a reply to the customer...'
              }
              disabled={isDisabled || isSending}
              style={{ minHeight: '80px', maxHeight: '200px' }}
              className={cn(
                'w-full resize-none rounded-xl border px-3.5 py-3 text-sm leading-relaxed',
                'text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 dark:placeholder:text-zinc-500',
                'focus:outline-none focus:ring-2 transition-all duration-150',
                'disabled:opacity-60 disabled:cursor-not-allowed',
                isInternal
                  ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 focus:ring-amber-400/30 focus:border-amber-400'
                  : 'bg-zinc-50 dark:bg-zinc-800/50 border-zinc-200 dark:border-zinc-700 focus:ring-blue-500/30 focus:border-blue-500'
              )}
            />
          </div>

          <div className="flex flex-col gap-1.5 shrink-0">
            {onUpload && (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={pendingAttachments.length >= MAX_ATTACHMENTS || isUploading || isDisabled || isSending}
                className="h-9 w-9 rounded-xl bg-zinc-100 dark:bg-zinc-800 ring-1 ring-zinc-200 dark:ring-zinc-700 flex items-center justify-center hover:bg-zinc-200 dark:hover:bg-zinc-700 disabled:opacity-40 transition-colors"
              >
                {isUploading
                  ? <div className="h-3.5 w-3.5 rounded-full border-2 border-zinc-400 border-t-transparent animate-spin" />
                  : <Paperclip className="h-3.5 w-3.5 text-zinc-500" />
                }
              </button>
            )}
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSend}
              className={cn(
                'h-9 w-9 rounded-xl flex items-center justify-center transition-colors shadow-md disabled:opacity-40 disabled:cursor-not-allowed',
                isInternal
                  ? 'bg-amber-500 hover:bg-amber-600 shadow-amber-500/20'
                  : 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20'
              )}
            >
              {isSending
                ? <div className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                : <Send className="h-3.5 w-3.5 text-white" />
              }
            </button>
          </div>
        </div>

        <p className="mt-2 flex items-center gap-1 text-[11px] text-zinc-400 dark:text-zinc-500">
          <CornerDownLeft className="h-3 w-3" />
          <kbd className="font-mono">⌘↵</kbd> to send
          {content.length > 0 && (
            <span className="ml-auto font-mono">{content.length}</span>
          )}
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".pdf,.doc,.docx,.xls,.xlsx,image/*,.txt"
        onChange={handleFileSelect}
      />
    </div>
  );
}
